/**
 * Validation + scoring helpers for the onboarding wizard.
 * Messages mirror PeakApple `Registration/Validation.swift`.
 */

export const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
export const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;
export const RESERVED_USERNAMES = ["admin", "root", "peak", "support", "help", "api", "www"];

export function emailError(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return "Email is required.";
  if (!EMAIL_REGEX.test(trimmed)) return "Enter a valid email.";
  return null;
}

export function passwordError(password: string): string | null {
  if (!password) return "Password is required.";
  if (password.length < 6) return "Password must be at least 6 characters.";
  return null;
}

export function usernameSyntaxError(username: string): string | null {
  if (!username) return "Username is required.";
  if (username.length < 3) return "Must be at least 3 characters.";
  if (username.length > 20) return "Must be 20 characters or fewer.";
  if (!USERNAME_REGEX.test(username)) return "Letters, numbers and underscores only.";
  if (RESERVED_USERNAMES.includes(username.toLowerCase())) return "That username is reserved.";
  return null;
}

export function isValidDob(dob: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return false;
  const [y, m, d] = dob.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return false;
  if (y < 1920) return false;
  return date.getTime() <= Date.now();
}

/** 1→20, 2→35, 3→50, 4→65, 5→80, 6→90, 7→100 (iOS WellnessScore.pct). */
export function wellnessPct(value: number): number {
  switch (Math.min(Math.max(Math.round(value), 1), 7)) {
    case 1: return 20;
    case 2: return 35;
    case 3: return 50;
    case 4: return 65;
    case 5: return 80;
    case 6: return 90;
    default: return 100;
  }
}

export interface WellnessInputs {
  health: number;
  mood: number;
  physical: number;
  recovery: number;
  sleep: number;
  stress: number;
  sleepTime: number;
}

/**
 * iOS `WellnessScore.calculate`:
 * floor((pct(physical)+pct(mood)+2·pct(recovery)+pct(sleep)+pct(stress)+pct(health)+1.5·pct(sleepTime)) / 8.5)
 */
export function wellnessScore(w: WellnessInputs): number {
  const total =
    wellnessPct(w.physical) +
    wellnessPct(w.mood) +
    2 * wellnessPct(w.recovery) +
    wellnessPct(w.sleep) +
    wellnessPct(w.stress) +
    wellnessPct(w.health) +
    1.5 * wellnessPct(w.sleepTime);
  return Math.floor(total / 8.5);
}

/** YYYYMMDD for today in local time (matches dayPosts convention). */
export function todayDateID(now: Date = new Date()): string {
  return (
    now.getFullYear().toString() +
    (now.getMonth() + 1).toString().padStart(2, "0") +
    now.getDate().toString().padStart(2, "0")
  );
}
